import { inject, Injectable } from "@angular/core";
import { FiatDepositFacade } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit.facade";
import { combineLatest, Observable } from "rxjs";
import { CryptoDepositViewData } from "@modules/+new/payments/features/crypto-deposit/domain/entities/crypto-deposit-view-data";
import { map, switchMap, take, tap } from "rxjs/operators";
import { CryptoDepositStateService } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-state.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { FiatDepositStepsParams } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-params.model";
import { FiatDepositNavigationService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit-navigation.service";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { CryptoWallet } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Modal, ModalService } from "@core-services/modal.service";
import { WalletSelectionModalComponent } from "@modules/main/components/tradable-to-view-item/tradable-to-wallet/wallet-selection-modal/wallet-selection-modal.component";
import { MultipleWalletsTrackingService } from "@main-services/wallet/multiple-wallets-tracking.service";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { CryptoDepositRemote } from "@modules/+new/payments/features/crypto-deposit/domain/infrastructure/crypto-deposit.remote";
import { DepositWalletAddress } from "@modules/+new/payments/features/crypto-deposit/domain/entities/deposit-wallet-address.model";

@Injectable()
export class CryptoDepositFacade {
	private cryptoDepositStateService = inject(CryptoDepositStateService);
	public availableCryptoList$: Observable<PaymentsCryptocoin[]> =
		this.depositCryptocoinsPersistenceState.availableCryptoDepositList$;
	constructor(
		private fiatDepositFacade: FiatDepositFacade,
		private fiatDepositNavigationService: FiatDepositNavigationService,
		private tradableWalletService: CryptoWalletService,
		private modalService: ModalService,
		private modal: UniversalModalService,
		private multipleWalletsTrackingService: MultipleWalletsTrackingService,
		private depositCryptocoinsPersistenceState: CryptocoinsState,
		private depositCryptocoinsRemote: CryptoDepositRemote
	) {}

	public getCryptoDepositViewData$(): Observable<CryptoDepositViewData> {
		if (this.cryptoDepositStateService.isViewDataStateLoaded()) {
			return this.cryptoDepositStateService.getViewData$();
		}
		return this.getCryptoDepositViewDataBase$().pipe(
			tap((data: CryptoDepositViewData) => {
				this.cryptoDepositStateService.setViewData(data);
			}),
			switchMap(() => this.cryptoDepositStateService.getViewData$())
		);
	}

	public closeModal = ($event?: MouseEvent): void => {
		$event?.preventDefault();
		this.modal.close(ModalCloseReason.close);
	};

	public backToStep = (stepName: FiatDepositStepNames, $event?: MouseEvent): void => {
		$event?.preventDefault();
		return this.navigateToStep(stepName, { fromBack: true });
	};

	private getCryptoDepositViewDataBase$(): Observable<CryptoDepositViewData> {
		return combineLatest([this.fiatDepositFacade.getStepsData$(), this.availableCryptoList$]).pipe(
			map(([stepData, cryptoList]) => {
				const selectedCrypto = cryptoList.find((crypto) => crypto.symbol === stepData.assetSymbol);
				const wallet = this.tradableWalletService.getItemById(stepData.walletGuid);
				return {
					wallet,
					selectedCryptocoin: selectedCrypto,
					availableNetworks: selectedCrypto?.networks.filter(
						(network) => network.is_operational && network.is_deposit_allowed
					),
					unavailableNetworks: selectedCrypto?.networks.filter(
						(network) => !network.is_operational && network.is_deposit_allowed
					),
					selectedNetwork: selectedCrypto?.networks.find((network) => network.id === stepData.networkId)
				};
			})
		);
	}

	public getAddressByCoinNetworkId(coin_network_id: string, walletId?: string): Observable<DepositWalletAddress> {
		return this.depositCryptocoinsRemote.getAddressByCoinNetworkId(coin_network_id, walletId);
	}

	public navigateToStep(stepName: FiatDepositStepNames, params?: FiatDepositStepsParams): void {
		this.fiatDepositNavigationService.navigateToStep$(stepName, params).pipe(take(1)).subscribe();
	}

	public getWalletsByAssetId(assetId: string): CryptoWallet[] {
		return this.tradableWalletService.getWalletsByTradableId(assetId);
	}

	public navigateToMultipleWallets(wallets: TradableWallet[], tradableId: string): Modal {
		const selectionModal: Modal<WalletSelectionModalComponent> = this.modalService
			.create(WalletSelectionModalComponent, {
				tradableWallets: wallets,
				tradableId: tradableId
			})
			.present();
		this.multipleWalletsTrackingService.trackMultipleWalletScreenViewed();
		return selectionModal;
	}

	public navigateToCryptoWalletDetails(cryptoSymbol: string, networkId: string, walletId: string): void {
		this.navigateToStep(FiatDepositStepNames.DepositCryptoWallet, {
			assetSymbol: cryptoSymbol,
			networkId: networkId,
			walletGuid: walletId
		});
	}

	public selectNetwork(network: string, cryptocoin: PaymentsCryptocoin): void {
		this.navigateToStep(FiatDepositStepNames.CryptoDepositSummary, {
			assetSymbol: cryptocoin.symbol,
			networkId: network
		});
	}

	public navigateFromSummary(network: string, cryptocoin: PaymentsCryptocoin): void {
		const wallets = this.getWalletsByAssetId(cryptocoin.cryptocoin_id.toString());
		if (wallets.length > 1) {
			this.closeModal();
			const selectionModal = this.navigateToMultipleWallets(wallets, cryptocoin.cryptocoin_id.toString());
			selectionModal.onDismiss.subscribe((res) => {
				const selectedWallet: TradableWallet = res.data;
				if (!selectedWallet) {
					return;
				}
				this.multipleWalletsTrackingService.trackMultipleWalletClicked();
				//navigate to info screen
				//will change network to real id when the BE is ready
				this.navigateToStep(FiatDepositStepNames.CryptoDepositWarning, {
					assetSymbol: cryptocoin.symbol,
					networkId: network,
					walletGuid: selectedWallet.getId()
				});
			});
		} else {
			this.navigateToStep(FiatDepositStepNames.CryptoDepositWarning, {
				assetSymbol: cryptocoin.symbol,
				networkId: network,
				walletGuid: wallets[0].getId()
			});
		}
	}

	public navigateToAssetSelection(): void {
		this.navigateToStep(FiatDepositStepNames.SelectCryptoWallet);
	}

	public navigateToNetworkSelection(assetSymbol: string): void {
		this.navigateToStep(FiatDepositStepNames.SelectNetwork, { assetSymbol });
	}

	public navigateToDepositSummary(assetSymbol: string, networkId: string): void {
		this.navigateToStep(FiatDepositStepNames.CryptoDepositSummary, {
			assetSymbol: assetSymbol,
			networkId: networkId
		});
	}

	public navigateToNonDefaultAddress(assetSymbol: string, networkId: string): void {
		this.navigateToStep(FiatDepositStepNames.DepositNonDefaultAddress, {
			assetSymbol: assetSymbol,
			networkId: networkId
		});
	}

	public getCoinByCryptocoinId(id: string): Observable<PaymentsCryptocoin> {
		return this.availableCryptoList$.pipe(
			map((list) => {
				return list.find((item) => item.cryptocoin_id.toString() === id);
			})
		);
	}

	public getNetworkName(id: string): Observable<string> {
		return this.getCoinByCryptocoinId(id).pipe(
			map((coin) => {
				const network = coin.networks.find((net) => net.id === coin.default_network_id);
				return network ? network.name : "";
			})
		);
	}
}
